// Cargar un producto desde el PDF del proveedor (ficha técnica, informe de inspección de fábrica) —
// ADR-2026-10-18-el-producto-se-carga-desde-el-pdf-del-proveedor.
//
// El PDF se lee EN EL NAVEGADOR (texto + fotos embebidas, `utils/pdf/leerPdf.ts`) y al backend van
// sólo el texto y unas hojas de contacto con las fotos numeradas: Haiku devuelve la propuesta
// (nombre, características, medidas, paquete de una unidad, qué fotos usar). Nada se sube hasta que
// la persona revisa y toca Guardar, y entonces sólo las fotos elegidas.
import { atlasBackendFetch } from './atlasBackend';

export interface FichaDesdePdf {
  nombre: string;
  variosProductos: boolean;
  campos: Array<{ clave: string; valor: string }>;
  medidas: { unidad: 'cm' | 'mm' | 'm'; alto: number | null; ancho: number | null; profundidad: number | null; espesor: number | null; peso_kg: number | null } | null;
  paquete: { alto: number | null; ancho: number | null; profundidad: number | null; peso_kg: number | null } | null;
  fotos: number[];
  dudas: string[];
}

export interface FotoDelPdfLeida {
  numero: number;
  pagina: number;
  blob: Blob;
}

export interface PdfPreparado {
  nombreArchivo: string;
  texto: string;
  fotos: FotoDelPdfLeida[];
  /** Hojas de contacto en base64 (JPEG, sin el prefijo `data:`). */
  hojas: string[];
}

/** Lo que se manda del texto: suficiente para un informe largo, sin llenar el pedido. */
export const MAX_TEXTO_PDF = 30_000;
/** Un PDF de proveedor normal pesa menos; más es probablemente un catálogo entero. */
export const MAX_BYTES_PDF = 60 * 1024 * 1024;

/** El texto de las páginas con su número, cortado al tope. */
export function textoParaFicha(textoPorPagina: string[]): string {
  return textoPorPagina
    .map((t, i) => (t.trim() ? `── Página ${i + 1} ──\n${t.trim()}` : ''))
    .filter(Boolean)
    .join('\n\n')
    .slice(0, MAX_TEXTO_PDF);
}

/** `data:image/jpeg;base64,AAAA` → `AAAA`. */
export const base64DeDataUrl = (url: string): string => url.slice(url.indexOf(',') + 1);

/** Lee el PDF en el navegador: texto, fotos numeradas y hojas de contacto. */
export async function prepararPdf(archivo: File): Promise<PdfPreparado> {
  if (archivo.size > MAX_BYTES_PDF) throw new Error('El PDF es muy grande (más de 60 MB).');
  const { leerPdf, extraerFotosDelPdf, hojasDeContacto } = await import('../utils/pdf/leerPdf');
  const bytes = new Uint8Array(await archivo.arrayBuffer());
  const { textoPorPagina } = await leerPdf(bytes);
  const { fotos } = await extraerFotosDelPdf(bytes);
  const numeradas = fotos.map((f, i) => ({ numero: i + 1, pagina: f.pagina, blob: f.blob }));
  const hojas = numeradas.length > 0 ? await hojasDeContacto(numeradas, numeradas.map((f) => f.numero)) : [];
  return { nombreArchivo: archivo.name, texto: textoParaFicha(textoPorPagina), fotos: numeradas, hojas: hojas.map(base64DeDataUrl) };
}

/** Haiku puede tardar ~15 s con un informe de 21 páginas; más de esto ya no vale la pena. */
const TIMEOUT_MS = 90_000;

/** Pide la propuesta al backend. Nunca tira: sin propuesta, `error` en castellano. */
export async function pedirFichaDesdePdf(p: PdfPreparado): Promise<{ ficha: FichaDesdePdf | null; error: string | null }> {
  const ctrl = new AbortController();
  const reloj = window.setTimeout(() => ctrl.abort(), TIMEOUT_MS);
  try {
    const res = await atlasBackendFetch(
      '/v1/products/ficha-desde-pdf',
      {
        nombre_archivo: p.nombreArchivo,
        texto: p.texto,
        fotos: p.fotos.map(({ numero, pagina }) => ({ numero, pagina })),
        hojas: p.hojas.map((data) => ({ mime: 'image/jpeg', data })),
      },
      { signal: ctrl.signal },
    );
    const json = (await res.json().catch(() => null)) as { ficha?: FichaDesdePdf; error?: string } | null;
    if (!res.ok || !json?.ficha) return { ficha: null, error: json?.error || 'No pude leer el PDF. Probá de nuevo o cargá el producto a mano.' };
    return { ficha: json.ficha, error: null };
  } catch (e) {
    const abortada = e instanceof DOMException && e.name === 'AbortError';
    return { ficha: null, error: abortada ? 'Leer el PDF tardó demasiado. Probá de nuevo.' : 'No pude conectarme para leer el PDF.' };
  } finally {
    window.clearTimeout(reloj);
  }
}

/** Lo que se guarda en la ficha después de crear el producto. */
export type DatosDelPdf = Pick<FichaDesdePdf, 'campos' | 'medidas' | 'paquete' | 'dudas' | 'variosProductos'>;

export const datosDeLaFicha = (f: FichaDesdePdf): DatosDelPdf => ({
  campos: f.campos,
  medidas: f.medidas,
  paquete: f.paquete,
  dudas: f.dudas,
  variosProductos: f.variosProductos,
});

/**
 * Los datos del PDF que le tocan a un producto de la subida de varios. Primero los de la unidad
 * misma; si no, los de la carpeta de alguna de sus fotos: "Añadir a otro producto" mueve las fotos
 * de un PDF a otra unidad y sus datos quedaban colgados de la clave vieja. Si se juntaron dos PDF,
 * gana el del destino (ADR-2026-10-19-revision-greptile-pdf-y-catalogo).
 */
export function datosDelPdfDeLaUnidad(
  claveDeLaUnidad: string,
  clavesDeSusCarpetas: ReadonlyArray<string | undefined>,
  porClave: ReadonlyMap<string, DatosDelPdf>,
): { clave: string; datos: DatosDelPdf } | null {
  for (const clave of [claveDeLaUnidad, ...clavesDeSusCarpetas]) {
    const datos = clave ? porClave.get(clave) : undefined;
    if (clave && datos) return { clave, datos };
  }
  return null;
}

/**
 * El nombre del producto cuando llega la propuesta de un PDF: se respeta el que escribió la persona,
 * pero el que había puesto un PDF anterior se reemplaza por el de este.
 */
export function nombreTrasLeerPdf(actual: string, puestoPorElPdfAnterior: string | null, delPdf: string): string {
  const escrito = actual.trim();
  return escrito && escrito !== puestoPorElPdfAnterior ? actual : delPdf;
}

/** Leer el PDF y pedir la propuesta, en un paso. Nunca tira. */
export async function leerPdfDelProveedor(
  archivo: File,
): Promise<{ ficha: FichaDesdePdf | null; fotos: FotoDelPdfLeida[]; error: string | null }> {
  try {
    const preparado = await prepararPdf(archivo);
    const { ficha, error } = await pedirFichaDesdePdf(preparado);
    return { ficha, fotos: preparado.fotos, error };
  } catch (err) {
    return { ficha: null, fotos: [], error: err instanceof Error ? err.message : 'No pude abrir ese PDF.' };
  }
}

/**
 * Guarda los datos del PDF en la ficha del producto recién creado (medidas, características y caja
 * de envío). Devuelve la línea de qué quedó; si algo falla, el producto ya existe y se completa
 * desde Mi marca.
 */
export async function guardarDatosDelPdf(varianteId: string, nombre: string, datos: DatosDelPdf): Promise<string> {
  if (datos.campos.length === 0 && !datos.medidas && !datos.paquete) return '';
  const [{ guardarDatosDelProducto }, api] = await Promise.all([
    import('../pages/v2/guardarDatosDelProducto'),
    import('../pages/v2/medidasApi'),
  ]);
  return guardarDatosDelProducto(
    { producto_id: varianteId, medidas: datos.medidas, campos: datos.campos, paquete: datos.paquete },
    {
      nombreDelProducto: () => nombre || 'El producto',
      leerMedidas: async (id) => (await api.fetchMedidasPorVariante([id]))[id] ?? null,
      guardarMedidas: api.saveMedidasDeVariante,
      leerFicha: async (id) => (await api.fetchFichaPorVariante([id]))[id] ?? { campos: [] },
      guardarFicha: api.saveFichaDeVariante,
      // La persona revisó la propuesta antes de crear: es un dato confirmado.
      guardarPaquete: (id, p, o) => api.savePaqueteDeVariante(id, p, { ...o, origen: 'usuario' }),
    },
  ).catch((err) => `No guardé la ficha: ${String(err)}`);
}
